import React from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

// ---- shared data hooks ----
export function useProviders() {
  return useQuery({
    queryKey: ["providers"],
    queryFn: async () => (await api.get("/api/edi/note-to-claim/providers")).data?.providers ?? [],
    staleTime: 20_000,
  });
}
export function useClients() {
  return useQuery({
    queryKey: ["clients"],
    queryFn: async () => (await api.get("/api/edi/note-to-claim/clients")).data?.clients ?? [],
    staleTime: 20_000,
  });
}
export function useProfile() {
  return useQuery({
    queryKey: ["profile"],
    queryFn: async () => (await api.get("/api/ehibridge/auth/profile")).data,
    staleTime: 60_000,
  });
}

// ---- presentational primitives (revQ system) ----
export function ScreenHead({ eyebrow, title, lede, action, badge }) {
  return (
    <div className="flex items-start gap-4 mb-2">
      <div className="flex-1 min-w-0">
        {eyebrow ? (
          <div className="text-[11.5px] font-bold tracking-[.06em] uppercase text-[var(--gray2)] mb-1.5">{eyebrow}</div>
        ) : null}
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-[18px] font-bold tracking-[-.01em] leading-[1.3] text-[var(--ink)]">{title}</h2>
          {badge ? (
            <span className="text-[11.5px] font-bold tracking-[.06em] uppercase rounded-full px-2.5 py-[3px] bg-[#F1F1F4] text-[var(--gray2)]">{badge}</span>
          ) : null}
        </div>
        {lede ? <p className="text-[13px] text-[var(--gray2)] mt-1.5 leading-relaxed max-w-[68ch]">{lede}</p> : null}
      </div>
      {action || null}
    </div>
  );
}

export function Section({ title, action, children, testid }) {
  return (
    <section className="mt-7 first:mt-7" data-testid={testid}>
      {(title || action) && (
        <div className="flex items-center justify-between gap-3 mb-2.5">
          <span className="text-[13px] font-semibold text-[var(--gray2)]">{title}</span>
          {action || null}
        </div>
      )}
      {children}
    </section>
  );
}

export function Field({ label, children }) {
  return (
    <div className="py-3 border-b border-[#E9E9EC]">
      <div className="text-[12px] text-[var(--gray2)]">{label}</div>
      <div className="text-[13px] text-[var(--ink)] mt-0.5">{children || "—"}</div>
    </div>
  );
}

// Bordered input style shared across Practice Setup (matches other controls).
export const PS_INPUT = "w-full rounded-[10px] border border-black/[.14] bg-white px-3 py-2 text-[13px] font-medium text-[var(--ink)] outline-none focus:border-[#5856D6] transition placeholder:text-[var(--gray3)] placeholder:font-normal";

// NPPES names come in ALL CAPS — title-case them for titles & sentences.
export function titleCase(s) {
  if (!s) return s;
  return s.split(" ").map((w) => {
    if (w.includes(".") && /^[A-Za-z]\.?([A-Za-z]\.?)*$/.test(w)) return w.toUpperCase();
    if (["MD", "DO", "PA", "NP", "PHD"].includes(w.toUpperCase().replace(/[^A-Z]/g, ""))) return w.toUpperCase();
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  }).join(" ");
}
// "JACK GOLDMAN, M.D." -> "Jack Goldman" (for possessive sentences)
export function providerShort(name) {
  return titleCase((name || "").split(",")[0].trim());
}

export function ListCard({ children, testid }) {
  return (
    <div data-testid={testid} className="rounded-[14px] overflow-hidden border border-[#E9E9EC] bg-white">
      {children}
    </div>
  );
}

export function Empty({ children }) {
  return (
    <div className="py-10 text-center text-[13px] text-[var(--gray2)] bg-white border border-dashed border-[#E9E9EC] rounded-[14px]">
      {children}
    </div>
  );
}

export function Skeletons({ n = 3 }) {
  return (
    <div className="rounded-[14px] overflow-hidden border border-[#E9E9EC] bg-white">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className={`flex items-center gap-3 px-4 py-3.5 ${i ? "border-t border-[#E9E9EC]" : ""}`}>
          <div className="w-[38px] h-[38px] rounded-full bg-[#F1F1F4] animate-pulse" />
          <div className="flex-1">
            <div className="h-3 w-40 bg-black/[.06] rounded animate-pulse" />
            <div className="h-2.5 w-24 bg-black/[.05] rounded animate-pulse mt-2" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function Avatar({ text, dark }) {
  const initials = (text || "?").split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  return (
    <div className={`w-[38px] h-[38px] rounded-full flex items-center justify-center text-[12.5px] font-bold flex-shrink-0 ${dark ? "bg-[var(--ink)] text-white" : "bg-[#F1F1F4] text-[var(--gray2)]"}`}>
      {initials}
    </div>
  );
}

// Amber "someone has to act" chip
export function ActionChip({ children }) {
  return (
    <span className="inline-flex items-center gap-1 text-[9.5px] font-bold px-2 py-0.5 rounded-[6px]" style={{ background: "#FBF0DF", color: "#9A5A00", border: "1px solid #F0DEC0" }}>
      {children}
    </span>
  );
}

// "Needs API · DQ-n" marker (Full state only). Matches the mock .mk chip: transparent
// fill, indigo text + hairline indigo border, indigo dot. Opens the DQ drawer.
export function DqMarker({ n, onOpen }) {
  return (
    <span
      data-testid={`ps-dq-marker-${n}`}
      className="inline-flex items-center gap-1.5 flex-none text-[11.5px] font-semibold px-2.5 py-[3px] rounded-full bg-transparent"
      style={{ color: "#4A4AC2", border: "1px solid rgba(88,86,214,.35)" }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#5856D6" }} />
      Needs API
    </span>
  );
}

// A Full-only "gap row": named field/feature with no endpoint yet.
export function GapRow({ title, desc, dq, onOpen }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3.5 border-t border-[#E9E9EC] first:border-t-0 min-h-[56px]">
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-[var(--ink2)]">{title}</div>
        {desc ? <div className="text-[12px] text-[var(--gray2)] mt-0.5">{desc}</div> : null}
      </div>
      <DqMarker n={dq} onOpen={onOpen} />
    </div>
  );
}

export function GapList({ items, onOpen }) {
  return (
    <div className="rounded-[14px] overflow-hidden border border-[#E9E9EC] bg-white">
      {items.map((it) => (
        <GapRow key={it.dq} {...it} onOpen={onOpen} />
      ))}
    </div>
  );
}

export function ErrLine({ children }) {
  if (!children) return null;
  return (
    <div className="mt-2 flex items-start gap-1.5 text-[11.5px] text-[#B3261E]">
      <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
      <span className="whitespace-pre-wrap break-words">{children}</span>
    </div>
  );
}
